import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Upload, FileSpreadsheet, Check, X, AlertTriangle, Edit2, Plus, Loader2, ArrowRight, Download, FileText } from "lucide-react";
import PdfImport from "./PdfImport";

const LINE_STATUS_STYLES = {
  READY: "bg-[hsl(148_40%_88%)] text-[hsl(var(--forest))]",
  UNMATCHED: "bg-[hsl(38_90%_88%)] text-[hsl(35_75%_38%)]",
  UNKNOWN_SKU: "bg-[hsl(6_60%_92%)] text-[hsl(6_62%_45%)]",
  MISSING_DATE_LOCATION_TYPE: "bg-[hsl(6_60%_92%)] text-[hsl(6_62%_45%)]",
  BAD_QTY: "bg-[hsl(6_60%_92%)] text-[hsl(6_62%_45%)]",
  MISSING_COST: "bg-[hsl(38_90%_88%)] text-[hsl(35_75%_38%)]",
  ALREADY_POSTED: "bg-[hsl(var(--rule))] text-[hsl(var(--ink-soft))]",
};

const TEMPLATES = [
  { key: "purchase_in", label: "Purchase In", desc: "GRN / direct purchase from vendor" },
  { key: "items_out", label: "Items Out", desc: "Issue to kitchen, wastage, staff meal" },
  { key: "stock_count", label: "Stock Count", desc: "Physical count with variance" },
  { key: "item_master", label: "Item Master", desc: "Bulk create items" },
  { key: "opening_stock", label: "Opening Stock", desc: "Initial stock on hand" },
];

export default function ImportCentre({ items, locations, onRefresh }) {
  const { user } = useAuth();
  const isDirector = user?.role === "admin";
  const [channel, setChannel] = useState("excel"); // excel | pdf
  const [view, setView] = useState("upload"); // upload | mapping | review
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const fileRef = useRef(null);

  const loadBatches = useCallback(async () => {
    try {
      const { data } = await api.get("/staff/inventory/v2/batches", { params: { limit: 50 } });
      setBatches((data.rows || []).filter(b => b.channel === "excel" || b.channel === "pdf"));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadBatches(); }, [loadBatches]);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 20 * 1024 * 1024) { toast.error("File exceeds 20 MB"); return; }
    setFile(f);
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", f);
      const { data } = await api.post("/staff/inventory/v2/import/preview", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setMapping(data.guessed_mapping);
      setView("mapping");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview failed");
    } finally { setUploading(false); }
  };

  const confirmImport = async () => {
    if (!preview) return;
    setConfirming(true);
    try {
      const { data } = await api.post("/staff/inventory/v2/import/confirm", {
        file_sha256: preview.file_sha256,
        filename: preview.filename,
        mapping,
        lines: preview.parsed_lines,
        channel: "excel",
      });
      toast.success(`Created ${data.created} draft batch(es)`);
      setPreview(null);
      setFile(null);
      setView("upload");
      loadBatches();
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Confirm failed");
    } finally { setConfirming(false); }
  };

  const openBatch = async (batch) => {
    try {
      const { data } = await api.get(`/staff/inventory/v2/batches/${batch.id}`);
      setSelectedBatch(data);
      setView("review");
    } catch { toast.error("Could not load batch"); }
  };

  const postBatch = async (batchId) => {
    try {
      const { data } = await api.post(`/staff/inventory/v2/batches/${batchId}/post`);
      if (data.needs_approval) {
        toast.warning(`Sent to approvals: ${data.rule_hits.map(h => h.rule).join(", ")}`);
      } else {
        toast.success(`Posted — ${data.ledger_rows.length} ledger row(s)`);
      }
      setSelectedBatch(null);
      setView("upload");
      loadBatches();
      onRefresh();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(d?.errors ? d.errors.map(e => e.detail).join(" · ") : (typeof d === "string" ? d : "Post failed"));
    }
  };

  const readyCount = preview?.parsed_lines?.filter(l => l.line_status === "READY").length || 0;
  const totalCount = preview?.parsed_lines?.length || 0;

  return (
    <div data-testid="import-centre" className="space-y-4">
      {/* Channel switch */}
      {view === "upload" && (
        <div className="flex gap-1 p-1 bg-[hsl(var(--bg-2))] rounded-md w-fit">
          <button
            data-testid="channel-excel"
            onClick={() => setChannel("excel")}
            className={`px-4 py-2 text-xs uppercase tracking-wider rounded ${channel === "excel" ? "bg-[hsl(var(--forest))] text-[hsl(var(--bg))]" : "text-[hsl(var(--ink-soft))]"}`}
          >
            <FileSpreadsheet size={12} className="inline mr-1.5" /> Excel / CSV
          </button>
          <button
            data-testid="channel-pdf"
            onClick={() => setChannel("pdf")}
            className={`px-4 py-2 text-xs uppercase tracking-wider rounded ${channel === "pdf" ? "bg-[hsl(var(--forest))] text-[hsl(var(--bg))]" : "text-[hsl(var(--ink-soft))]"}`}
          >
            <FileText size={12} className="inline mr-1.5" /> PDF / Photo
          </button>
        </div>
      )}

      {/* PDF channel */}
      {view === "upload" && channel === "pdf" && (
        <PdfImport items={items} locations={locations} onDone={() => { loadBatches(); onRefresh(); }} />
      )}

      {/* Templates (Excel only) */}
      {view === "upload" && channel === "excel" && (
        <div className="p-4 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))]">
          <div className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))] mb-2">Download templates</div>
          <div className="flex flex-wrap gap-2">
            {TEMPLATES.map(t => (
              <a
                key={t.key}
                href={`${process.env.REACT_APP_BACKEND_URL}/api/staff/inventory/v2/import/templates/${t.key}`}
                download
                className="inline-flex items-center gap-2 px-3 py-2 text-xs border border-[hsl(var(--rule))] hover:border-[hsl(var(--forest))] hover:text-[hsl(var(--forest))]"
                title={t.desc}
              >
                <Download size={12} /> {t.label}
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Upload zone (Excel only) */}
      {view === "upload" && channel === "excel" && (
        <div
          data-testid="import-dropzone"
          onClick={() => fileRef.current?.click()}
          className="p-8 border-2 border-dashed border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] text-center cursor-pointer hover:border-[hsl(var(--forest))] transition-colors"
        >
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleFile} className="hidden" />
          {uploading ? (
            <Loader2 size={32} className="mx-auto animate-spin text-[hsl(var(--ink-soft))]" />
          ) : (
            <>
              <Upload size={32} className="mx-auto text-[hsl(var(--ink-soft))]" />
              <div className="mt-2 font-semibold">Drop Excel or CSV here</div>
              <div className="text-xs text-[hsl(var(--ink-soft))] mt-1">.xlsx, .xls, .csv — max 20 MB</div>
            </>
          )}
        </div>
      )}

      {/* Column mapping */}
      {view === "mapping" && preview && (
        <div className="space-y-4">
          <div className="p-4 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))]">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold">{preview.filename}</div>
                <div className="text-xs text-[hsl(var(--ink-soft))]">{preview.total_rows} rows · {readyCount} ready, {totalCount - readyCount} need attention</div>
              </div>
              <button onClick={() => { setView("upload"); setPreview(null); }} className="text-sm text-[hsl(var(--ink-soft))]">
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Mapping table */}
          <div className="border border-[hsl(var(--rule))]">
            <div className="p-2 bg-[hsl(var(--bg-2))] text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
              Column mapping — confirm or adjust
            </div>
            <div className="divide-y divide-[hsl(var(--rule))]">
              {Object.entries(mapping).map(([stdCol, srcIdx]) => (
                <div key={stdCol} className="p-2 flex items-center gap-3 text-sm">
                  <div className="w-32 font-mono text-xs">{stdCol}</div>
                  <ArrowRight size={14} className="text-[hsl(var(--ink-soft))]" />
                  <select
                    data-testid={`map-${stdCol}`}
                    value={srcIdx ?? ""}
                    onChange={e => setMapping({ ...mapping, [stdCol]: e.target.value === "" ? null : Number(e.target.value) })}
                    className="tbb-inp flex-1 text-sm"
                  >
                    <option value="">— Not mapped —</option>
                    {preview.headers.map((h, i) => (
                      <option key={i} value={i}>{h}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>

          {/* Preview first 5 rows */}
          <div className="border border-[hsl(var(--rule))] overflow-x-auto">
            <div className="p-2 bg-[hsl(var(--bg-2))] text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
              Preview (first 5 rows)
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[hsl(var(--rule))]">
                  <th className="p-2 text-left">#</th>
                  <th className="p-2 text-left">Item</th>
                  <th className="p-2 text-right">Qty</th>
                  <th className="p-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {preview.parsed_lines.slice(0, 5).map(l => (
                  <tr key={l.row_index} className="border-b border-[hsl(var(--rule))]">
                    <td className="p-2">{l.row_index}</td>
                    <td className="p-2 max-w-[200px] truncate">{l.raw_item_text || l.sku || "—"}</td>
                    <td className="p-2 text-right">{l.qty ?? "—"}</td>
                    <td className="p-2">
                      <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${LINE_STATUS_STYLES[l.line_status] || ""}`}>
                        {l.line_status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button
            data-testid="import-confirm"
            onClick={confirmImport}
            disabled={confirming || readyCount === 0}
            className="btn-brass !py-3 !px-6 text-sm w-full disabled:opacity-50"
          >
            {confirming ? <Loader2 size={16} className="animate-spin inline" /> : `Import ${readyCount} ready line${readyCount === 1 ? "" : "s"} as draft batch`}
          </button>
          {readyCount < totalCount && (
            <div className="text-xs text-[hsl(35_75%_38%)] text-center">
              {totalCount - readyCount} line{totalCount - readyCount === 1 ? "" : "s"} will be skipped (unmatched)
            </div>
          )}
        </div>
      )}

      {/* Review grid */}
      {view === "review" && selectedBatch && (
        <ReviewGrid
          batch={selectedBatch}
          items={items}
          onClose={() => { setSelectedBatch(null); setView("upload"); loadBatches(); }}
          onPost={() => postBatch(selectedBatch.id)}
          onRefresh={onRefresh}
        />
      )}

      {/* Batch list (both channels) */}
      {view === "upload" && batches.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">Recent import batches</div>
          {batches.map(b => (
            <div
              key={b.id}
              data-testid={`import-batch-${b.id}`}
              onClick={() => openBatch(b)}
              className="p-3 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] cursor-pointer hover:border-[hsl(var(--forest))] flex items-center justify-between"
            >
              <div>
                <div className="font-mono text-sm text-[hsl(var(--brass-2))]">{b.batch_no}</div>
                <div className="text-xs text-[hsl(var(--ink-soft))]">{b.date} · {b.location} · {b.lines?.length || 0} lines</div>
              </div>
              <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${b.status === "posted" ? "bg-[hsl(148_40%_88%)] text-[hsl(var(--forest))]" : b.status === "pending_approval" ? "bg-[hsl(38_90%_88%)] text-[hsl(35_75%_38%)]" : "bg-[hsl(var(--rule))] text-[hsl(var(--ink-soft))]"}`}>
                {b.status.replace(/_/g, " ")}
              </span>
            </div>
          ))}
        </div>
      )}

      <style>{`.tbb-inp{background:hsl(var(--bg));border:1px solid hsl(var(--rule));padding:0.4rem 0.6rem;font-size:0.85rem}.tbb-inp:focus{outline:1px solid hsl(var(--forest));border-color:hsl(var(--forest))}`}</style>
    </div>
  );
}


function ReviewGrid({ batch, items, onClose, onPost, onRefresh }) {
  const [resolving, setResolving] = useState(null); // line index
  const [resolveSku, setResolveSku] = useState("");
  const [saveAlias, setSaveAlias] = useState(true);
  const [creatingItem, setCreatingItem] = useState(false);
  const [newItem, setNewItem] = useState({ name: "", category: "Dry Goods", base_uom: "kg", purchase_uom_label: "", base_per_purchase: "" });
  const [busy, setBusy] = useState(false);

  const lines = batch.lines || [];
  const readyCount = lines.filter(l => l.sku).length;
  const allReady = readyCount === lines.length && lines.length > 0;

  // Bill total check
  const billTotal = batch.bill_total || lines.reduce((s, l) => s + (l.qty_base || 0) * (l.unit_cost || 0), 0);
  const computedTotal = lines.reduce((s, l) => s + (l.qty_base || 0) * (l.unit_cost || 0), 0);
  const totalDiff = Math.abs(billTotal - computedTotal);
  const hasMismatch = totalDiff > 1;

  const resolveLine = async (lineIdx) => {
    if (!resolveSku) { toast.error("Pick an item"); return; }
    setBusy(true);
    try {
      await api.post(`/staff/inventory/v2/import/batches/${batch.id}/lines/${lineIdx}/resolve`, {
        sku: resolveSku,
        save_alias: saveAlias,
        alias_text: lines[lineIdx].raw_item_text,
        vendor: batch.invoice_no || "",
      });
      toast.success("Line resolved");
      setResolving(null);
      setResolveSku("");
      onRefresh();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Resolve failed");
    } finally { setBusy(false); }
  };

  const createItemInline = async (lineIdx) => {
    if (!newItem.name.trim()) { toast.error("Item name required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/staff/inventory/v2/import/batches/${batch.id}/lines/${lineIdx}/create-item`, {
        ...newItem,
        base_per_purchase: newItem.base_per_purchase ? Number(newItem.base_per_purchase) : null,
        save_alias: true,
      });
      toast.success(`Created ${data.item.sku}`);
      setCreatingItem(false);
      setNewItem({ name: "", category: "Dry Goods", base_uom: "kg", purchase_uom_label: "", base_per_purchase: "" });
      onRefresh();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="review-grid" className="space-y-4">
      {/* Header */}
      <div className="p-4 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))]">
        <div className="flex items-start justify-between">
          <div>
            <div className="font-mono text-sm text-[hsl(var(--brass-2))]">{batch.batch_no}</div>
            <div className="font-semibold">{batch.movement_type.replace(/_/g, " ")} · {batch.location}</div>
            <div className="text-xs text-[hsl(var(--ink-soft))]">{batch.date} · {lines.length} lines · {batch.source_file}</div>
          </div>
          <button onClick={onClose} className="text-[hsl(var(--ink-soft))]"><X size={16} /></button>
        </div>
        {/* Bill total check */}
        <div className={`mt-3 p-2 text-xs ${hasMismatch ? "bg-[hsl(6_60%_95%)] text-[hsl(6_62%_45%)]" : "bg-[hsl(148_40%_92%)] text-[hsl(var(--forest))]"}`}>
          Bill total: ₹{billTotal.toLocaleString("en-IN", { maximumFractionDigits: 2 })} ·
          Computed: ₹{computedTotal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
          {hasMismatch && ` · Difference: ₹${totalDiff.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`}
        </div>
      </div>

      {/* Lines */}
      <div className="border border-[hsl(var(--rule))] divide-y divide-[hsl(var(--rule))]">
        {lines.map((l, i) => {
          const isReady = !!l.sku;
          const isResolving = resolving === i;
          const isCreating = creatingItem === i;
          return (
            <div key={i} data-testid={`review-line-${i}`} className={`p-3 ${isReady ? "" : "bg-[hsl(38_90%_96%)]"}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm">{l.raw_item_text || l.item_name}</div>
                  <div className="text-xs text-[hsl(var(--ink-soft))]">
                    {l.qty_entered} {l.uom_entered} → {l.qty_base} {l.uom_entered === "purchase" ? "base" : ""}
                    {l.unit_cost && ` · ₹${l.unit_cost}/unit`}
                  </div>
                  {isReady && (
                    <div className="text-xs text-[hsl(var(--forest))] mt-1">
                      → {l.item_name} ({l.sku})
                      {l.match_source === "fuzzy" && ` · ${(l.match_confidence * 100).toFixed(0)}% match`}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${isReady ? LINE_STATUS_STYLES.READY : LINE_STATUS_STYLES.UNMATCHED}`}>
                    {isReady ? "READY" : "UNMATCHED"}
                  </span>
                  {!isReady && !isResolving && !isCreating && (
                    <div className="flex gap-1">
                      <button onClick={() => setResolving(i)} className="text-xs px-2 py-1 border border-[hsl(var(--forest))] text-[hsl(var(--forest))]">
                        Map
                      </button>
                      <button onClick={() => { setCreatingItem(i); setNewItem({ ...newItem, name: l.raw_item_text }); }} className="text-xs px-2 py-1 border border-[hsl(var(--brass))] text-[hsl(var(--brass-2))]">
                        <Plus size={12} />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Resolve panel */}
              {isResolving && (
                <div className="mt-3 p-3 border border-[hsl(var(--rule))] bg-[hsl(var(--bg))]">
                  <div className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))] mb-2">Map to existing item</div>
                  <select value={resolveSku} onChange={e => setResolveSku(e.target.value)} className="tbb-inp w-full text-sm">
                    <option value="">— Select item —</option>
                    {items.map(it => <option key={it.sku} value={it.sku}>{it.name} ({it.sku})</option>)}
                  </select>
                  <label className="flex items-center gap-2 mt-2 text-xs">
                    <input type="checkbox" checked={saveAlias} onChange={e => setSaveAlias(e.target.checked)} className="w-4 h-4" />
                    Save "{l.raw_item_text}" as alias for future imports
                  </label>
                  <div className="mt-2 flex gap-2">
                    <button onClick={() => resolveLine(i)} disabled={busy || !resolveSku} className="text-xs px-3 py-1.5 bg-[hsl(var(--forest))] text-[hsl(var(--bg))] disabled:opacity-50">
                      {busy ? "Saving..." : "Resolve"}
                    </button>
                    <button onClick={() => setResolving(null)} className="text-xs px-3 py-1.5 text-[hsl(var(--ink-soft))]">Cancel</button>
                  </div>
                </div>
              )}

              {/* Create item panel */}
              {isCreating && (
                <div className="mt-3 p-3 border border-[hsl(var(--brass))] bg-[hsl(var(--bg))]">
                  <div className="text-xs uppercase tracking-wider text-[hsl(var(--brass-2))] mb-2">Create new item</div>
                  <input value={newItem.name} onChange={e => setNewItem({ ...newItem, name: e.target.value })} placeholder="Item name" className="tbb-inp w-full text-sm mb-2" />
                  <div className="grid grid-cols-2 gap-2">
                    <select value={newItem.category} onChange={e => setNewItem({ ...newItem, category: e.target.value })} className="tbb-inp text-sm">
                      {["Dry Goods", "Dairy", "Produce", "Meat & Seafood", "Beverages", "Coffee", "Bakery", "Packaging", "Cleaning", "Consumables"].map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <select value={newItem.base_uom} onChange={e => setNewItem({ ...newItem, base_uom: e.target.value })} className="tbb-inp text-sm">
                      {["kg", "g", "L", "ml", "pc"].map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                    <input value={newItem.purchase_uom_label} onChange={e => setNewItem({ ...newItem, purchase_uom_label: e.target.value })} placeholder="Pack label (e.g. Bag 25 kg)" className="tbb-inp text-sm" />
                    <input type="number" value={newItem.base_per_purchase} onChange={e => setNewItem({ ...newItem, base_per_purchase: e.target.value })} placeholder="Base per pack" className="tbb-inp text-sm" />
                  </div>
                  <div className="mt-2 flex gap-2">
                    <button onClick={() => createItemInline(i)} disabled={busy} className="text-xs px-3 py-1.5 bg-[hsl(var(--brass))] text-[hsl(var(--bg))] disabled:opacity-50">
                      {busy ? "Creating..." : "Create & resolve"}
                    </button>
                    <button onClick={() => setCreatingItem(false)} className="text-xs px-3 py-1.5 text-[hsl(var(--ink-soft))]">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Post button */}
      {allReady && (
        <button data-testid="review-post" onClick={onPost} className="btn-brass !py-3 !px-6 text-sm w-full">
          Post batch ({readyCount} lines)
        </button>
      )}
      {!allReady && (
        <div className="text-xs text-center text-[hsl(35_75%_38%)]">
          {readyCount} of {lines.length} lines ready — resolve or skip the rest
        </div>
      )}

      <style>{`.tbb-inp{background:hsl(var(--bg));border:1px solid hsl(var(--rule));padding:0.4rem 0.6rem;font-size:0.85rem}.tbb-inp:focus{outline:1px solid hsl(var(--forest));border-color:hsl(var(--forest))}`}</style>
    </div>
  );
}
